import { applyDateYearClamp, DATE_INPUT_MAX } from '../lib/dateInput';

/**
 * 날짜 입력 연도 4자리 전역 가드
 *
 * 증상 — <input type="date"> 에 `max` 가 없으면 Chrome 은 연도를 6자리까지 받는다.
 * 그래서 연도 네 자리를 치고 나서도 월로 넘어가지 않고 계속 연도 칸에 쌓인다.
 * 실측(2026-10-02): 발주일에 "20251231" 을 치면 `0231-10-02` 가 됐다 — 여덟 자리가 전부 연도로 갔다.
 *
 * 고치는 방법은 값을 다시 쓰는 게 아니라 **`max` 를 주는 것**이다. 상한이 있으면 Chrome 이
 * 연도를 네 자리로 제한하고 스스로 월 칸으로 넘어간다 (같은 입력이 `2025-12-31` 이 된다).
 * 반대로 입력 도중에 value 를 다시 쓰면 커서가 연도 칸으로 되돌아가 증상이 더 나빠진다 —
 * 이전 구현이 input 이벤트에서 값을 고쳐 쓰다가 그렇게 됐다.
 *
 * 화면마다 max 를 붙이는 방식은 반드시 빠지는 곳이 생긴다(날짜 입력 96개 / 45개 파일).
 * 그래서 여기서 한 번에 붙이고, 나중에 그려지는 입력은 MutationObserver 로 따라간다.
 * 값 보정(clamp)은 혹시 모를 경우를 위해 **focusout 에서만** 한다 — 타이핑을 방해하지 않는다.
 */
const applyMax = (el) => {
  if (!el || el.tagName !== 'INPUT' || el.type !== 'date') return;
  if (el.hasAttribute('max')) return;   // 화면이 직접 정한 상한은 건드리지 않는다
  el.setAttribute('max', DATE_INPUT_MAX);
};

const sweep = (root) => {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  if (root.tagName === 'INPUT') applyMax(root);
  root.querySelectorAll('input[type="date"]').forEach(applyMax);
};

export function initDateYearGuard(doc = (typeof document !== 'undefined' ? document : null)) {
  if (!doc || typeof window === 'undefined') return () => {};

  const descriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
  const nativeSet = descriptor && descriptor.set;

  sweep(doc.body || doc);

  // 포커스가 들어온 순간에도 한 번 — 관찰자가 놓친 경우의 안전망
  const onFocusIn = (e) => applyMax(e.target);
  doc.addEventListener('focusin', onFocusIn, true);

  // 타이핑이 끝난 뒤에만 값 보정 (max 로 막히지 않은 경로 대비)
  const onFocusOut = (e) => {
    if (!nativeSet) return;
    applyDateYearClamp(e.target, (fixed) => {
      nativeSet.call(e.target, fixed);
      e.target.dispatchEvent(new Event('input', { bubbles: true }));
      e.target.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };
  doc.addEventListener('focusout', onFocusOut, true);

  let observer = null;
  if (typeof MutationObserver !== 'undefined' && doc.body) {
    observer = new MutationObserver((records) => {
      records.forEach((r) => r.addedNodes && r.addedNodes.forEach(sweep));
    });
    observer.observe(doc.body, { childList: true, subtree: true });
  }

  return () => {
    doc.removeEventListener('focusin', onFocusIn, true);
    doc.removeEventListener('focusout', onFocusOut, true);
    if (observer) observer.disconnect();
  };
}

export default initDateYearGuard;
